import React, { type MouseEvent, type ComponentType, type ReactNode } from 'react';
import { useHref, useLinkClickHandler, useNavigate } from 'react-router-dom';
import { Button, Link, Space, Text } from '@g2u/ui-kit';

import env from 'env';

import { PopConfirm } from 'components/common/confirm';
import { Toast, isTimeExpired } from 'helpers';
import { encryptAesToken } from 'helpers/crypto';
import { Condition, type JobSearchResult } from 'types';

export namespace JobActions {
  type ActionFC = (props: JobSearchResult) => ReactNode;

  const isJobOpen = (record: JobSearchResult) =>
    record.completed !== Condition.Yes && record.cancelled !== Condition.Yes;

  const isJobPartsOnly = (record: JobSearchResult) => record.job_type === 'S';

  const ifAllowed = (key: string, Component: ComponentType<JobSearchResult>) =>
    function AllowedAction(record: JobSearchResult) {
      return isJobOpen(record) ? <Component key={key} {...record} /> : null;
    };

  const alwaysAllow = (key: string, Component: ComponentType<JobSearchResult>) =>
    function AllowedAction(record: JobSearchResult) {
      return <Component key={key} {...record} />;
    };

  export const ViewDetails: ActionFC = alwaysAllow('view-details', (record: JobSearchResult) => {
    return (
      <Link.$ href={`${env.LEGACY_BMS_URL}jobs/index/edit/id/${encryptAesToken(record.ID.toString())}`} target="_blank">
        Go to Core
      </Link.$>
    );
  });

  export const Edit: ActionFC = alwaysAllow('edit', (record: JobSearchResult) => {
    const path = `/job/edit/${record.ID}/summary`;
    const href = useHref(path);
    const handleClick = useLinkClickHandler(path);

    return (
      <Link.$ href={href} onClick={(e: MouseEvent<HTMLAnchorElement>) => handleClick(e)}>
        View / Edit
      </Link.$>
    );
  });

  export const ManageTickets: ActionFC = alwaysAllow('manageTickets', (record: JobSearchResult) => {
    const path = `/tasks/tickets?search_value=${record.ID}&search_type=service_id&status=1`;
    const href = useHref(path);
    const handleClick = useLinkClickHandler(path);

    return (
      <Link.$ href={href} onClick={(e: MouseEvent<HTMLAnchorElement>) => handleClick(e)}>
        Manage tickets
      </Link.$>
    );
  });

  export const Reschedule: ActionFC = ifAllowed('reschedule', (record: JobSearchResult) => {
    const path = `/job/reschedule/${record.ID}/summary`;
    const href = useHref(path);
    const handleClick = useLinkClickHandler(path);

    if (isJobPartsOnly(record)) return null;

    return (
      <Link.$ href={href} onClick={(e: MouseEvent<HTMLAnchorElement>) => handleClick(e)}>
        Reschedule
      </Link.$>
    );
  });

  export const Cancel: ActionFC = ifAllowed('cancel', (record: JobSearchResult) => {
    const path = `/job/cancel/${record.ID}`;
    const navigate = useNavigate();

    if (isJobPartsOnly(record)) {
      return (
        <Link.$
          role="button"
          onClick={() =>
            navigate(path, {
              state: { charges: Condition.No, returnTo: location.pathname },
            })
          }
        >
          <Text.$ type="danger">Cancel</Text.$>
        </Link.$>
      );
    }

    return (
      <PopConfirm.$
        title="Cancel this job"
        description={
          <Space.$ direction={Space.Direction.Vertical}>
            {isTimeExpired(record.appointment, record.customer_timezone, 24) ? (
              <>
                <Text.$>
                  There is less than 24 hours remaining before the appointment time. A 1-hour service fee will be
                  charged to the customer and credited to their account.
                </Text.$>
                <Text.$>Do you want to continue?</Text.$>
              </>
            ) : (
              <>
                <Text.$>
                  There is more than 24 hours remaining before the appointment time. The customer can cancel it without
                  any charge.
                </Text.$>
                <Text.$>Do you want to proceed?</Text.$>
              </>
            )}
          </Space.$>
        }
        actions={[
          {
            label: 'No',
          },
          ...(isTimeExpired(record.appointment, record.customer_timezone, 24)
            ? [
                {
                  label: 'Yes, but waive the fee',
                  action: () => {
                    navigate(path, {
                      state: { charges: Condition.No, returnTo: location.pathname },
                    });
                    Toast.show({
                      description: <>Cancellation charge has been waived for this job.</>,
                      type: Toast.Type.Success,
                    });
                  },
                },
              ]
            : []),
          {
            label: 'Yes',
            action: () => {
              navigate(path, {
                state: {
                  charges: isTimeExpired(record.appointment, record.customer_timezone, 24)
                    ? Condition.Yes
                    : Condition.No,
                  returnTo: `/job/edit/${record.ID}/service-details`,
                },
              });
            },
            type: Button.Type.Primary,
          },
        ]}
      >
        <div>
          <Link.$ role="button">
            <Text.$ type="danger">Cancel</Text.$>
          </Link.$>
        </div>
      </PopConfirm.$>
    );
  });
}
